import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getInvitation, acceptInvitation } from '../services/accountInvitationService';

// Generic accept-invitation page. Driven by the ?token=... URL param. The page
// is user-type-aware so the same component works for agents today and renters /
// owners later — the only thing that varies is the copy.
//
// Flow:
//   1. On mount, GET /account-invitations/{token} → fetch summary.
//   2. Display a confirmation with agency / user_type context.
//   3. On click "Accept invitation", POST /account-invitations/accept.
//   4. On success, redirect to /login with a one-shot success flash via the
//      location.state mechanism.
const AcceptAccountInvitation = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const data = await getInvitation(token);
        setSummary(data);
      } catch (err) {
        setError(err.message || 'Invitation could not be loaded.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      const res = await acceptInvitation(token);
      navigate('/login', {
        state: { flash: res.message || 'Account activated. You can now sign in.' },
        replace: true,
      });
    } catch (err) {
      setError(err.message || 'Failed to accept invitation.');
    } finally {
      setAccepting(false);
    }
  };

  // ---- copy ---------------------------------------------------------------
  const renderHeadline = () => {
    if (!summary) return 'Invitation';
    switch (summary.user_type) {
      case 'agent':
        return summary.agency_name
          ? `You've been invited to join ${summary.agency_name}`
          : "You've been invited to join an agency";
      case 'renter':
        return summary.agency_name
          ? `You've been invited as a renter for ${summary.agency_name}`
          : "You've been invited as a renter";
      case 'owner':
      default:
        return 'Confirm your account';
    }
  };

  const renderBody = () => {
    if (!summary) return null;
    switch (summary.user_type) {
      case 'agent':
        return (
          <p className="text-gray-700">
            Click <strong>Accept invitation</strong> to activate your agent account
            {summary.agency_name ? ` for ${summary.agency_name}` : ''}. After
            activation you'll be able to sign in with this email.
          </p>
        );
      case 'renter':
        return (
          <p className="text-gray-700">
            Click <strong>Accept invitation</strong> to activate your renter account.
            After activation you'll be able to sign in with this email.
          </p>
        );
      case 'owner':
      default:
        return (
          <p className="text-gray-700">
            Click <strong>Confirm</strong> to activate your account. After confirmation
            you'll be able to sign in with this email.
          </p>
        );
    }
  };

  // ---- render --------------------------------------------------------------
  return (
    <div className="min-h-screen flex items-center bg-gray-50">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white shadow-2xl border-0 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-center py-6">
            <h3 className="text-2xl font-bold mb-0">{renderHeadline()}</h3>
            {summary?.email && (
              <p className="text-emerald-100 mb-0 text-sm mt-1">{summary.email}</p>
            )}
          </div>

          <div className="p-6 space-y-5">
            {loading && (
              <div className="flex justify-center text-gray-500">
                <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {!loading && !error && summary && (
              <>
                {renderBody()}
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={accepting}
                  className="w-full px-4 py-3 text-lg font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {accepting ? 'Activating…' : (summary.user_type === 'owner' ? 'Confirm' : 'Accept invitation')}
                </button>
              </>
            )}

            <p className="text-sm text-center text-gray-500">
              <Link to="/login" className="text-emerald-600 hover:text-emerald-800 font-medium">
                Back to sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptAccountInvitation;

